import { Injectable } from "@angular/core";
import { Observable, Subject, firstValueFrom, switchMap } from "rxjs";
import { createUIError } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { cancelButton, dangerButton } from "@webapp/shared/modal/modal.utils";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { ConfirmDeleteSessionComponent, ConfirmDeleteSessionModalData } from "../components/confirm-delete-session/confirm-delete-session.component";
import { Session } from "../models/sessions.model";
import { SessionsRepository } from "./sessions-repository.service";

@Injectable()
export class ConfirmDeleteSessionService {
  constructor(private modalService: UiModalService, private sessionsRepository: SessionsRepository) {}

  public confirmDeleteSession(session: Session): Observable<void> {
    return this.sessionsRepository.checkForConflictsInSession$(session, "delete").pipe(
      switchMap((conflictsCol) => {
        return this.createModal({
          uiTitle: localize("confirm_delete_session"),
          uiData: {
            conflicts: conflictsCol.items,
            sessionId: session.id,
            itemName: session.title,
            description: localize("delete_session_warning_objectives_without_session"),
            toDeleteItemText: localize("to_delete_session_enter_name"),
            inputPlaceholder: "Enter session's name here",
          },
          action: () => firstValueFrom(this.sessionsRepository.delete$({ id: session.id })),
        });
      })
    );
  }

  private createModal(options: { uiTitle: string; uiData: ConfirmDeleteSessionModalData; action(): Promise<void> }): Observable<void> {
    const { uiTitle, uiData, action } = options;
    const subject = new Subject<void>();
    this.modalService.create<ConfirmDeleteSessionComponent, ConfirmDeleteSessionModalData>({
      uiTitle,
      uiContent: ConfirmDeleteSessionComponent,
      uiData,
      uiFooter: [
        dangerButton(localize("delete"), {
          show: (component) => !component.hasConflicts,
          disabled: (component) => !component.canDelete,
          onClick: (component) => {
            component.setDeletingProgress();
            return action().then(
              () => {
                subject.next();
                subject.complete();
              },
              (error) => {
                component.setDeletingError(createUIError(error).message);
                return false;
              }
            );
          },
        }),
        dangerButton(localize("ignore_and_delete"), {
          show: (component) => component.hasConflicts,
          onClick: (component) => {
            component.ignoreConflicts();
            return false;
          },
        }),
        cancelButton({
          onClick: () => {
            subject.complete();
          },
        }),
      ],
    });
    return subject;
  }
}
